import type { FC, ReactNode } from 'react'

import { ArrowUpRight, Brain } from 'lucide-react'
import Container from '@/components/landing/container'

import { DIAGNOSTIC_TESTS } from '@/constants/landing'

const DiagnosticsSection: FC = (): ReactNode => {
    return (
        <section id='tests' aria-labelledby='tests-title' className='pb-20 sm:pb-28'>
            <Container>
                <div className='reveal relative isolate overflow-hidden rounded-[2rem] bg-accent px-6 py-12 sm:px-12 sm:py-14 lg:px-16 lg:py-16'>
                    <div aria-hidden='true' className='absolute -right-24 -top-24 -z-10 size-80 rounded-full border-[40px] border-brand-teal/10' />
                    <div aria-hidden='true' className='absolute -bottom-32 right-1/3 -z-10 size-72 rounded-full bg-brand-teal/10 blur-2xl' />

                    <div className='grid items-center gap-10 lg:grid-cols-2 lg:gap-16'>
                        <div>
                            <span className='grid size-14 place-items-center rounded-2xl bg-brand-teal text-white shadow-lg shadow-brand-teal/30'>
                                <Brain className='size-7' aria-hidden='true' />
                            </span>

                            <h2 id='tests-title' className='mt-6 text-3xl font-semibold leading-tight text-foreground sm:text-4xl'>
                                Not sure where you fit? Take a diagnostic test.
                            </h2>

                            <p className='mt-4 max-w-lg text-lg leading-relaxed text-foreground/75'>
                                Uncover your personality traits and people skills with a Skillkafe diagnostic test, then choose the course that suits you best.
                            </p>
                        </div>

                        <ul className='grid gap-4 sm:grid-cols-2'>
                            {DIAGNOSTIC_TESTS.map(test => (
                                <li key={test.code}>
                                    <a
                                        href={test.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='group flex h-full flex-col rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5 transition-[translate,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:p-7'
                                    >
                                        <span className='font-heading text-4xl font-bold tracking-tight text-brand-teal-ink'>
                                            {test.code}
                                        </span>

                                        <span className='mt-2 text-lg font-semibold text-foreground'>
                                            {test.title}
                                        </span>

                                        <span className='mt-1 text-sm text-muted-foreground'>
                                            Personality traits and people skills
                                        </span>

                                        <span className='mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-primary'>
                                            Start your test
                                            <ArrowUpRight className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' aria-hidden='true' />
                                        </span>

                                        <span className='sr-only'>(opens in a new tab)</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Container>
        </section>
    )
}

export default DiagnosticsSection
